import { createFileRoute } from "@tanstack/react-router";
import ServiceN8n from "@/pages/ServiceN8n";
import {
  buildPageHead,
  breadcrumbSchema,
  serviceSchema,
  faqPageSchema,
} from "@/lib/seo";

const faqs = [
  {
    question: "What does an n8n workflow developer do?",
    answer:
      "An n8n workflow developer builds, orchestrates, and maintains self-hosted or cloud automation workflows in n8n, connecting CRMs, APIs, databases, and AI agents with custom error handling, retry logic, and code-level nodes that go far beyond what no-code tools like Zapier can do.",
  },
  {
    question: "Why hire an n8n developer from the Philippines?",
    answer:
      "Hiring an n8n workflow developer from the Philippines gives you senior-grade automation engineering at a fraction of US rates, with timezone overlap for US, UK, and Australian agencies and direct contractor engagement without agency markups.",
  },
  {
    question: "How much does it cost to hire an n8n workflow developer?",
    answer:
      "Engagements are scoped per project or retainer. Marjohn works as a remote contractor paid via low-fee cross-border transfers such as Wise. Book a free strategy call to receive a custom scope and quote for your n8n automation needs.",
  },
  {
    question: "Can n8n replace Zapier?",
    answer:
      "Yes. n8n replaces fragile Zapier stacks with self-hosted, code-level workflow control, lower per-task costs, custom JavaScript nodes, and production-grade error handling, ideal for agencies running high-volume or complex automations.",
  },
  {
    question: "What happens if a webhook fails at 3 AM?",
    answer:
      "Every workflow I build includes Error Trigger nodes with automatic retry and exponential backoff, fallback routes to secondary endpoints, and Slack/email alerts the moment a failure occurs. No silent failures, your team knows within 60 seconds.",
  },
  {
    question: "Do you build custom JavaScript code nodes in n8n?",
    answer:
      "Yes. When built-in nodes fall short, I deploy custom JavaScript inside any node for complex transforms, regex validation, and logic that no-code tools simply can't express.",
  },
  {
    question: "Can you connect AI agents and LLMs into n8n workflows?",
    answer:
      "Yes. I wire Vapi voice AI, OpenAI, and Claude into n8n workflows, transcribe calls, summarize notes, and auto-update deal health tags so reps spend time selling instead of writing notes.",
  },
  {
    question: "How do we get started without disrupting live operations?",
    answer:
      "Every engagement begins with an audit and staging blueprint. We test all workflows, webhooks, and sync logic in a sandbox environment before rollout, ensuring your active automations run 100% uninterrupted.",
  },
];

export const Route = createFileRoute("/n8n-workflow-developer")({
  head: () =>
    buildPageHead({
      title: "n8n Workflow Developer & API Expert | Hire in Philippines",
      description:
        "Hire an elite n8n workflow developer in the Philippines. I build robust API integrations, error handling, and automation orchestrations for US, UK, and AU agencies.",
      path: "/n8n-workflow-developer",
      keywords:
        "n8n workflow developer, n8n automation, API orchestration, hire n8n developer Philippines, workflow automation, n8n integration, API expert, automation engineer, n8n consultant",
      hreflang: [
        {
          hreflang: "en-US",
          href: "https://marjohnrobillo.me/n8n-workflow-developer",
        },
        {
          hreflang: "en-AU",
          href: "https://marjohnrobillo.me/ghl-automation-expert-australia",
        },
        {
          hreflang: "en-GB",
          href: "https://marjohnrobillo.me/revops-specialist-uk",
        },
        { hreflang: "x-default", href: "https://marjohnrobillo.me/" },
      ],
      schema: [
        serviceSchema({
          name: "n8n Workflow Development & API Orchestration",
          description:
            "Custom n8n workflow development, API integration, error handling, and automation orchestration for scaling agencies.",
          path: "/n8n-workflow-developer",
          serviceType: [
            "n8n Workflow Development",
            "API Integration",
            "Automation Orchestration",
            "Error Handling Engineering",
          ],
        }),
        breadcrumbSchema([
          { name: "Home", path: "" },
          { name: "n8n Workflow Developer", path: "/n8n-workflow-developer" },
        ]),
        faqPageSchema(faqs),
      ],
    }),
  component: ServiceN8n,
});
